import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { QueryClient, QueryConfig, ReduxQueryClient } from '../../domain/interfaces/QueryClient';
import { baseQuery } from '../../utils/baseQuery';

/**
 * RTK Query Adapter
 * -----------------
 * Infrastructure adapter using RTK Query
 * Implements QueryClient and ReduxQueryClient domain interfaces
 * This can be swapped with TanStack Query adapter without changing feature code
 */

/**
 * RTK Query Adapter Class
 * Implements the QueryClient and ReduxQueryClient domain interfaces using RTK Query
 */
export class RtkQueryAdapter implements ReduxQueryClient {
  // RTK Query API instance - complex generic types require flexible typing
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTK Query has complex type inference
  private readonly api: any;

  readonly clientId: string;
  readonly reducerPath: string; // Redux-specific, kept for backward compatibility
  readonly reducer: unknown;
  readonly middleware: unknown;
  readonly endpoints: QueryClient['endpoints'] = {} as QueryClient['endpoints'];

  constructor(config: QueryConfig) {
    // Use clientId, with reducerPath as fallback for backward compatibility
    const configWithReducerPath = config as QueryConfig & { reducerPath?: string };
    const reducerPath = config.clientId || configWithReducerPath.reducerPath || 'api';
    
    // Create RTK Query API instance
    // RTK Query has complex type inference - let TypeScript infer the type
    this.api = createApi({
      reducerPath,
      baseQuery: baseQuery() as BaseQueryFn<unknown, unknown, unknown>,
      tagTypes: config.tagTypes || [],
      keepUnusedDataFor: config.keepUnusedDataFor ?? 60, // Default to 60 seconds if not specified
      endpoints: () => ({}), // Endpoints are added by features via .injectEndpoints()
    });

    // Implement QueryClient interface
    this.clientId = reducerPath;
    this.reducerPath = reducerPath; // Redux-specific, kept for backward compatibility
    this.reducer = this.api.reducer;
    this.middleware = this.api.middleware;
    this.endpoints = this.api.endpoints as QueryClient['endpoints'];
  }

  /**
   * Get the underlying RTK Query API instance
   * This is needed for features that require RTK Query's .injectEndpoints() method
   * @returns RTK Query API instance
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTK Query has complex type inference
  getApi(): any {
    return this.api;
  }
}

/**
 * Create RTK Query API instance (for direct RTK Query usage)
 * This is used by feature API slices that need RTK Query's builder
 * Returns the RTK Query API instance that can be used with .injectEndpoints()
 * 
 * @param config - Query configuration (clientId, tagTypes, keepUnusedDataFor)
 * @returns RTK Query API instance
 */
export function createQueryApi(config: QueryConfig) {
  const configWithReducerPath = config as QueryConfig & { reducerPath?: string };
  const reducerPath = config.clientId || configWithReducerPath.reducerPath || 'api';
  return createApi({
    reducerPath,
    baseQuery: baseQuery() as BaseQueryFn<unknown, unknown, unknown>,
    tagTypes: config.tagTypes || [],
    keepUnusedDataFor: config.keepUnusedDataFor ?? 60, // Default to 60 seconds if not specified
    endpoints: () => ({}), // Endpoints are added by features via .injectEndpoints()
  });
}


